/**
 * Theme Context
 * ─────────────
 * Provides dark / light color sets to the entire app.
 * Persists preference to localStorage under 'kader-theme'.
 * Sets data-theme attribute on <body> for CSS-level overrides.
 *
 * Usage:
 *   const { colors, isDark, toggle } = useTheme();
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

export interface ThemeColors {
  // Backgrounds
  bgPrimary: string;
  bgSecondary: string;
  bgCard: string;
  bgCardHover: string;
  bgCoach: string;
  bgUser: string;
  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  // Structure
  border: string;
  // Accent
  accent: string;
  // Semantic
  green: string;
  yellow: string;
  red: string;
  blue: string;
  purple: string;
  cyan: string;
  hrRed: string;
}

export const darkColors: ThemeColors = {
  bgPrimary:    '#0A0A0A',
  bgSecondary:  '#0F0F0F',
  bgCard:       '#141414',
  bgCardHover:  '#1A1A1A',
  bgCoach:      '#161616',
  bgUser:       '#1C1708',
  textPrimary:  '#F2F2F2',
  textSecondary:'#8A8A8A',
  textMuted:    '#808080',  // ≥4.5:1 on cards (was #5A5A5A — WCAG AA fail)
  border:       '#1E1E1E',
  accent:       '#E8731A',
  green:        '#4A8C5C',
  yellow:       '#C4A24E',
  red:          '#E8593C',
  blue:         '#3B8BD4',
  purple:       '#8B6CC1',
  cyan:         '#5BA8C8',
  hrRed:        '#E8593C',
};

// Warm off-white palette — gender-neutral, still athletic.
// Coral accent replaces orange: energetic without reading "hardcore male".
export const lightColors: ThemeColors = {
  bgPrimary:    '#F8F6F3',
  bgSecondary:  '#F2EFE9',
  bgCard:       '#FFFFFF',
  bgCardHover:  '#F5F2EC',
  bgCoach:      '#EDF2F7',
  bgUser:       '#FFF8F0',
  textPrimary:  '#1A1A1A',
  textSecondary:'#5C5C5C',
  textMuted:    '#6E6E6E',  // ≥4.5:1 on white (was #9A9A9A — WCAG AA fail)
  border:       '#E2DDD6',
  accent:       '#D4614A',
  green:        '#3A7848',
  yellow:       '#B8922A',
  red:          '#CC4B30',
  blue:         '#2E75C4',
  purple:       '#6B4DB8',
  cyan:         '#3E8CAE',
  hrRed:        '#CC4B30',
};

interface ThemeContextValue {
  colors: ThemeColors;
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  colors: darkColors,
  isDark: true,
  toggle: () => {},
});

const STORAGE_KEY = 'kader-theme';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== 'light';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
    try { localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light'); } catch {}
  }, [isDark]);

  // Set initial attribute synchronously to avoid flash
  useEffect(() => {
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, []);

  const toggle = () => setIsDark(d => !d);
  const colors = isDark ? darkColors : lightColors;

  return (
    <ThemeContext.Provider value={{ colors, isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
